import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Chip,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  resolveLinkDescription,
  resolveLinkUrl,
} from "../utils/linkFields";
import {
  ArrowBack,
  Code,
  MenuBook,
  ExpandMore,
  Description,
  Download,
  Article,
  OpenInNew,
} from "@mui/icons-material";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import { useNavigate, useParams } from "react-router-dom";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";

import DeveloperGuideViewer from "../components/developer-guide/DeveloperGuideViewer";

const PRODUCT_DETAIL_TAB_SLUGS = [
  "overview",
  "api-specs",
  "developer-guides",
] as const;
import { EntityStatus, Product } from "../types";
import { apiService } from "../api/services/apiService";
import { formatApiDisplayName } from "../utils/apiDisplayName";

const ProductDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();

  const { tabIndex: selectedTab, setTabIndex: setSelectedTab } =
    useBookmarkableTab({ slugs: PRODUCT_DETAIL_TAB_SLUGS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<Product | null>(null);

  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);
  const [expandedApi, setExpandedApi] = useState<string | false>(false);

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!productId) return;
      setLoading(true);
      setError(null);
      try {
        const p = await apiService.getProduct(productId);
        setProduct(p);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch product",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  const parseSpecContent = (specString: string) => {
    try {
      return JSON.parse(specString);
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Product not found"}</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/product-catalog")}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  const productAPIs = product.apis || [];
  const productFAQs = product.faqs || [];
  const productDocuments = product.documents || [];
  const productLinks = product.links || [];
  const productDeveloperGuides = (product.developerGuides || []).filter(
    (g) => g.status === EntityStatus.ACTIVE,
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.paper",
          py: 3,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <IconButton
              onClick={() => navigate("/product-catalog")}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {product.name}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              ml: 7,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={product.type?.replace("_", " ") ?? "Product"}
              size="small"
              variant="outlined"
            />
            <Chip
              label={product.status}
              size="small"
              color={
                product.status === EntityStatus.ACTIVE ? "success" : "default"
              }
              variant="outlined"
            />
            {(product.tags ?? []).slice(0, 3).map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
            {(product.tags?.length ?? 0) > 3 && (
              <Chip
                label={`+${(product.tags?.length ?? 0) - 3} more`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ borderRadius: 2 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pr: 2,
            }}
          >
            <Tabs value={selectedTab} onChange={setSelectedTab}>
              <Tab
                icon={<Description />}
                iconPosition="start"
                label="Overview"
              />
              <Tab icon={<Code />} iconPosition="start" label="API Specs" />
              <Tab
                icon={<MenuBook />}
                iconPosition="start"
                label="Developer Guides"
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 4 }}>
            {selectedTab === 0 && (
              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Description
                  </Typography>
                  <Typography color="text.secondary">
                    {product.description || "No description available."}
                  </Typography>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                      Product Information
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Category
                          </Typography>
                          <Typography>
                            {product.categories?.[0]?.name || "Uncategorized"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={product.status}
                            size="small"
                            color={
                              product.status === EntityStatus.ACTIVE
                                ? "success"
                                : "default"
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>

                  {productFAQs.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 3, fontWeight: 600 }}
                      >
                        Frequently Asked Questions
                      </Typography>
                      {productFAQs.map((faq) => (
                        <Accordion
                          key={faq.uuid}
                          expanded={expandedFaq === faq.uuid}
                          onChange={(_, e) =>
                            setExpandedFaq(e ? faq.uuid : false)
                          }
                        >
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography fontWeight={600}>
                              {faq.question || faq.name}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography color="text.secondary">
                              {faq.answer ?? faq.description ?? "—"}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                      Tags & Categories
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {(product.tags ?? []).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {productDocuments.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Documents
                        </Typography>
                        <List dense>
                          {productDocuments.map((doc) => (
                            <ListItem key={doc.uuid}>
                              <ListItemIcon>
                                <Article fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={doc.name}
                                secondary={doc.description ?? undefined}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}

                    {productLinks.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, mt: 2, fontWeight: 600 }}
                        >
                          Links
                        </Typography>
                        <List dense>
                          {productLinks.map((link) => {
                            const href = resolveLinkUrl(link);
                            const secondary = resolveLinkDescription(link);
                            return (
                              <ListItem
                                key={link.uuid}
                                component={href ? "a" : "li"}
                                href={href || undefined}
                                target={href ? "_blank" : undefined}
                                rel={href ? "noopener noreferrer" : undefined}
                                sx={href ? { textDecoration: "none", color: "inherit" } : undefined}
                              >
                                <ListItemIcon>
                                  <OpenInNew fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={link.name}
                                  secondary={secondary || href || undefined}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {selectedTab === 1 && (
              <Box>
                {productAPIs.length > 0 ? (
                  productAPIs.map((api) => (
                    <Accordion
                      key={api.uuid}
                      expanded={expandedApi === api.uuid}
                      onChange={(_, e) =>
                        setExpandedApi(e ? api.uuid : false)
                      }
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography fontWeight={600}>
                          {formatApiDisplayName(api.name)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {api.specContent ? (
                          <SwaggerUI
                            spec={parseSpecContent(api.specContent)}
                          />
                        ) : (
                          <Button startIcon={<Download />}>
                            Download API Documentation
                          </Button>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <Typography color="text.secondary">
                    No API specs linked to this product.
                  </Typography>
                )}
              </Box>
            )}

            {selectedTab === 2 && (
              <Box>
                {productDeveloperGuides.length > 0 ? (
                  productDeveloperGuides.map((guide) => (
                    <Box key={guide.uuid} sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                        {guide.name}
                      </Typography>
                      <DeveloperGuideViewer guide={guide} />
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary">
                    No active developer guides linked to this product.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProductDetailPage;
